import * as React from "react"
import { useTranslation } from "react-i18next"
import { getIntlLocale } from "@/lib/i18n/format"
import { cn } from "@/lib/utils"
import { MessageContent } from "./message"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Download,
  ExternalLink,
  MoreHorizontal,
  ChevronDown,
  ChevronRight,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

export interface TableColumn {
  key: string
  header: string
  type?: "text" | "number" | "date" | "currency" | "boolean"
  width?: string | number
  align?: "left" | "center" | "right"
  sortable?: boolean
}

export interface TableRow {
  id: string
  [key: string]: any
}

export interface MessageTableProps
  extends React.HTMLAttributes<HTMLDivElement> {
  data: TableRow[]
  columns: TableColumn[]
  title?: string
  caption?: string
  maxRows?: number
  sortable?: boolean
  collapsible?: boolean
  initialCollapsed?: boolean
  showExport?: boolean
  exportFormats?: ("csv" | "json" | "excel")[]
  onExport?: (format: string, data: any) => void
  onRowClick?: (row: TableRow) => void
  rowClassName?: (row: TableRow, index: number) => string
  cellClassName?: (value: any, column: TableColumn, row: TableRow) => string
}

const MessageTable = React.forwardRef<HTMLDivElement, MessageTableProps>(
  (
    {
      className,
      data,
      columns,
      title,
      caption,
      maxRows = 10,
      sortable = false,
      collapsible = false,
      initialCollapsed = false,
      showExport = true,
      exportFormats = ["csv", "json"],
      onExport,
      onRowClick,
      rowClassName,
      cellClassName,
      children,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const [sortColumn, setSortColumn] = React.useState<string | null>(null)
    const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">(
      "asc",
    )
    const [isCollapsed, setIsCollapsed] = React.useState(initialCollapsed)
    const [showAllRows, setShowAllRows] = React.useState(false)

    const sortedData = React.useMemo(() => {
      if (!sortable || !sortColumn) return data

      return [...data].sort((a, b) => {
        const aValue = a[sortColumn]
        const bValue = b[sortColumn]

        let comparison = 0
        if (aValue < bValue) comparison = -1
        if (aValue > bValue) comparison = 1

        return sortDirection === "desc" ? -comparison : comparison
      })
    }, [data, sortColumn, sortDirection, sortable])

    const displayData = showAllRows ? sortedData : sortedData.slice(0, maxRows)
    const hasMoreRows = sortedData.length > maxRows

    const handleSort = (columnKey: string) => {
      if (!sortable) return

      if (sortColumn === columnKey) {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc")
      } else {
        setSortColumn(columnKey)
        setSortDirection("asc")
      }
    }

    const formatCellValue = (value: any, column: TableColumn) => {
      if (value === null || value === undefined) return ""

      switch (column.type) {
        case "currency":
          return new Intl.NumberFormat(getIntlLocale("en-US"), {
            style: "currency",
            currency: "USD",
          }).format(value)

        case "number":
          return new Intl.NumberFormat(getIntlLocale()).format(value)

        case "date":
          return new Date(value).toLocaleDateString(getIntlLocale())

        case "boolean":
          return value ? t("common.yes") : t("common.no")

        default:
          return String(value)
      }
    }

    const getCellAlignment = (column: TableColumn) => {
      if (column.align) return column.align

      switch (column.type) {
        case "number":
        case "currency":
          return "right"
        case "boolean":
          return "center"
        default:
          return "left"
      }
    }

    const exportToCSV = () => {
      const csvContent = [
        columns.map((col) => col.header).join(","),
        ...data.map((row) =>
          columns
            .map((col) => {
              const value = formatCellValue(row[col.key], col)
              return `"${String(value).replace(/"/g, '""')}"`
            })
            .join(","),
        ),
      ].join("\n")

      const blob = new Blob([csvContent], { type: "text/csv" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `${title || "table"}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }

    const exportToJSON = () => {
      const jsonContent = JSON.stringify(data, null, 2)
      const blob = new Blob([jsonContent], { type: "application/json" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `${title || "table"}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }

    const handleExport = (format: string) => {
      if (onExport) {
        onExport(format, data)
        return
      }

      switch (format) {
        case "csv":
          exportToCSV()
          break
        case "json":
          exportToJSON()
          break
        default:
          console.warn(`Export format "${format}" not supported`)
      }
    }

    if (collapsible && isCollapsed) {
      return (
        <MessageContent ref={ref} {...props}>
          <div className={cn("rounded-lg border bg-card p-3", className)}>
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                className="flex h-auto items-center gap-2 p-0 font-medium"
                onClick={() => setIsCollapsed(false)}
              >
                <ChevronRight className="h-4 w-4" />
                {title || t("chat.messageTable.table")}
                <span className="text-xs text-muted-foreground">
                  {t("chat.messageTable.rowCount", { count: data.length })}
                </span>
              </Button>
            </div>
          </div>
        </MessageContent>
      )
    }

    return (
      <MessageContent ref={ref} {...props}>
        <div className={cn("rounded-lg border bg-card", className)}>
          {/* Header */}
          {(title || showExport || collapsible) && (
            <div className="flex items-center justify-between border-b p-3">
              <div className="flex items-center gap-2">
                {collapsible && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => setIsCollapsed(true)}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                )}
                {title && <h4 className="text-sm font-medium">{title}</h4>}
                <span className="text-xs text-muted-foreground">
                  {t("chat.messageTable.rowCount", { count: data.length })}
                </span>
              </div>

              {showExport && exportFormats.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6">
                      <MoreHorizontal className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {exportFormats.includes("csv") && (
                      <DropdownMenuItem onClick={() => handleExport("csv")}>
                        <Download className="mr-2 h-4 w-4" />
                        {t("chat.messageTable.exportCsv")}
                      </DropdownMenuItem>
                    )}
                    {exportFormats.includes("json") && (
                      <DropdownMenuItem onClick={() => handleExport("json")}>
                        <Download className="mr-2 h-4 w-4" />
                        {t("chat.messageTable.exportJson")}
                      </DropdownMenuItem>
                    )}
                    {exportFormats.includes("excel") && (
                      <DropdownMenuItem onClick={() => handleExport("excel")}>
                        <Download className="mr-2 h-4 w-4" />
                        {t("chat.messageTable.exportExcel")}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {columns.map((column) => (
                    <TableHead
                      key={column.key}
                      className={cn(
                        "text-xs font-medium",
                        getCellAlignment(column) === "right" && "text-right",
                        getCellAlignment(column) === "center" && "text-center",
                        sortable &&
                          column.sortable !== false &&
                          "cursor-pointer hover:bg-muted/50",
                      )}
                      style={{ width: column.width }}
                      onClick={() =>
                        column.sortable !== false && handleSort(column.key)
                      }
                    >
                      <div className="flex items-center gap-1">
                        {column.header}
                        {sortable &&
                          column.sortable !== false &&
                          sortColumn === column.key && (
                            <span className="text-xs">
                              {sortDirection === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayData.map((row, rowIndex) => (
                  <TableRow
                    key={row.id}
                    className={cn(
                      "text-sm",
                      onRowClick && "cursor-pointer",
                      rowClassName?.(row, rowIndex),
                    )}
                    onClick={() => onRowClick?.(row)}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        className={cn(
                          "py-2",
                          getCellAlignment(column) === "right" && "text-right",
                          getCellAlignment(column) === "center" &&
                            "text-center",
                          cellClassName?.(row[column.key], column, row),
                        )}
                      >
                        {formatCellValue(row[column.key], column)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Footer */}
          {(hasMoreRows || caption) && (
            <div className="border-t p-3 text-xs text-muted-foreground">
              {hasMoreRows && !showAllRows && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 text-xs text-blue-600 hover:text-blue-800"
                  onClick={() => setShowAllRows(true)}
                >
                  {t("chat.messageTable.showAllRows", { value: data.length })}
                </Button>
              )}
              {caption && <div className="mt-1">{caption}</div>}
            </div>
          )}
        </div>

        {children}
      </MessageContent>
    )
  },
)

MessageTable.displayName = "MessageTable"

export { MessageTable }
